import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  getDispatches, 
  getDispatch, 
  getSettings, 
  updateSettings,
  getCustomers 
} from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { canEdit } from '../lib/utils';
import { openPrintWindow } from '../lib/printWindow';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Loader2, 
  Save,
  Eye,
  Printer,
  FileText,
  Package,
  Truck,
  MapPin,
  ArrowLeft,
  Edit,
  RefreshCw
} from 'lucide-react';

// Default pallet label template
const DEFAULT_PALLET_TEMPLATE = {
  name: 'Pallet Label',
  countryOfOrigin: 'IRELAND',
  refrigerationWarning: 'PLEASE REFRIGERATE ON DELIVERY',
  showCompanyLogo: true,
  transporterAddress: '',
  topMarginMm: 30, // Higher margin for taping
  fontSize: {
    heading: 28,
    large: 22,
    medium: 16,
    small: 12
  }
};

// Default Aldi template settings (used for PDF generation + reset)
const DEFAULT_ALDI_SETTINGS = {
  from_line1: 'J & L Grubb Ltd',
  from_line2: 't/a Cashel Farmhouse Cheesemakers',
  from_line3: 'Beechmount, Fethard',
  from_line4: 'Co. Tipperary, Ireland',
  supplier_number: '',
  contact_name: '',
  contact_tel: '',
  contact_email: '',
};

// Default Agropur template settings (used for PDF generation + reset)
const DEFAULT_AGROPUR_SETTINGS = {
  customer_name: 'AGROPUR',
  address_line1: '4600 rue Armand-Frappier,',
  address_line2: 'St-Hubert, QC, J3Z 1G5',
  address_country: 'CANADA',
  coa_email: 'HUB.ImportsQA@agropur.com',
  supplier_name: 'CASHEL FARMHOUSE CHEESEMAKERS, IRELAND',
};

export default function PalletLabelDesigner() {
  const navigate = useNavigate();
  const { dispatchId } = useParams();
  const { user } = useAuth();
  const printRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dispatches, setDispatches] = useState([]);
  const [selectedDispatchId, setSelectedDispatchId] = useState(dispatchId || '');
  const [dispatch, setDispatch] = useState(null);
  const [settings, setSettings] = useState({});
  
  // Editor mode
  const [editorMode, setEditorMode] = useState(false);
  
  // Pallet label data (editable)
  const [labelData, setLabelData] = useState({
    customerName: '',
    customerDeliveryAddress: '',
    customerContactName: '',
    customerContactPhone: '',
    transporterName: '',
    transporterAddress: '',
    countryOfOrigin: 'IRELAND',
    contents: '',
    poNumber: '',
    palletNumber: '1',
    totalPallets: '1',
    totalCases: '',
    refrigerationWarning: 'PLEASE REFRIGERATE ON DELIVERY',
  });
  
  // Template settings
  const [template, setTemplate] = useState(DEFAULT_PALLET_TEMPLATE);
  
  // Aldi & Agropur per-template settings
  const [aldiSettings, setAldiSettings] = useState({ ...DEFAULT_ALDI_SETTINGS });
  const [agropurSettings, setAgropurSettings] = useState({ ...DEFAULT_AGROPUR_SETTINGS });
  const [activeTemplateTab, setActiveTemplateTab] = useState('standard');

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (selectedDispatchId) {
      loadDispatchData(selectedDispatchId);
    }
  }, [selectedDispatchId]);

  const loadData = async () => {
    try {
      const [dispatchesRes, settingsRes] = await Promise.allSettled([
        getDispatches({ limit: 50 }),  // Get recent dispatches regardless of status
        getSettings()
      ]);
      
      const dispatchesData = dispatchesRes.status === 'fulfilled' ? dispatchesRes.value.data || [] : [];
      const settingsData = settingsRes.status === 'fulfilled' ? settingsRes.value.data || {} : {};
      
      setDispatches(dispatchesData);
      setSettings(settingsData);
      
      // Load saved pallet template if exists
      if (settingsData?.pallet_label_template) {
        setTemplate(prev => ({ ...prev, ...settingsData.pallet_label_template }));
      }
      if (settingsData?.aldi_template_settings) {
        setAldiSettings(prev => ({ ...prev, ...settingsData.aldi_template_settings }));
      }
      if (settingsData?.agropur_template_settings) {
        setAgropurSettings(prev => ({ ...prev, ...settingsData.agropur_template_settings }));
      }
      
      // If dispatchId provided in URL, select it and load it directly
      if (dispatchId) {
        setSelectedDispatchId(dispatchId);
        // Load dispatch data directly
        try {
          const response = await getDispatch(dispatchId);
          if (response.data) {
            setDispatch(response.data);
            populateLabelFromDispatch(response.data);
          }
        } catch (err) {
          console.error('Failed to load dispatch:', err);
          toast.error('Failed to load dispatch for pallet label');
        }
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };
  
  const populateLabelFromDispatch = (d) => {
    // Build contents from dispatch lines
    const contents = (d.lines || [])
      .map(line => `${line.product_name || 'Product'} - Qty: ${line.quantity}`)
      .join('\n');
    
    // Calculate total cases
    const totalCases = (d.lines || []).reduce((sum, line) => sum + (line.cases || line.calculated_cases || 0), 0);
    
    setLabelData({
      customerName: d.customer_name || '',
      customerDeliveryAddress: d.delivery_address || '',
      customerContactName: d.delivery_contact_name || '',
      customerContactPhone: d.delivery_contact_phone || '',
      transporterName: d.vehicle_info || '',
      transporterAddress: template.transporterAddress || '',
      countryOfOrigin: template.countryOfOrigin || 'IRELAND',
      contents: contents,
      poNumber: d.po_number || d.dispatch_number || '',
      dispatchNumber: d.dispatch_number || '',
      palletNumber: '1',
      totalPallets: d.num_pallets?.toString() || '1',
      totalCases: totalCases.toFixed(0),
      refrigerationWarning: template.refrigerationWarning || 'PLEASE REFRIGERATE ON DELIVERY',
    });
  };

  const loadDispatchData = async (id) => {
    try {
      const response = await getDispatch(id);
      const d = response.data;
      setDispatch(d);
      populateLabelFromDispatch(d);
    } catch (error) {
      toast.error('Failed to load dispatch');
    }
  };

  const handleSaveTemplate = async () => {
    setSaving(true);
    try {
      await updateSettings({
        pallet_label_template: template,
        aldi_template_settings: aldiSettings,
        agropur_template_settings: agropurSettings,
      });
      toast.success('All pallet label templates saved');
    } catch (error) {
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleResetAldi = () => {
    setAldiSettings({ ...DEFAULT_ALDI_SETTINGS });
    toast.info('Aldi template reset to defaults (save to apply)');
  };

  const handleResetAgropur = () => {
    setAgropurSettings({ ...DEFAULT_AGROPUR_SETTINGS });
    toast.info('Agropur template reset to defaults (save to apply)');
  };

  const handlePrint = () => {
    
    const labelHtml = generatePrintHtml();
    openPrintWindow(labelHtml);
    
  };

  const generatePrintHtml = () => {
    const topMargin = template.topMarginMm || 20;
    const showCompanyLogo = template.showCompanyLogo !== false;
    const showTransporter = template.showTransporter !== false;
    const showContents = template.showContents !== false;
    const showPalletNumber = template.showPalletNumber !== false;
    const showCases = template.showCases !== false;
    
    // Count number of content lines for dynamic sizing
    const contentLines = (labelData.contents || '').split('\n').length;
    const addressLines = (labelData.customerDeliveryAddress || '').split('\n').length;
    
    // Adjust font sizes based on content amount
    const contentFontSize = contentLines > 5 ? '10pt' : '11pt';
    const addressFontSize = addressLines > 3 ? '12pt' : '13pt';
    
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Pallet Label - ${labelData.poNumber}</title>
        <style>
          @page {
            size: A4 portrait;
            margin: ${topMargin}mm 10mm 10mm 10mm;
          }
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          html, body {
            height: 100%;
            font-family: Arial, Helvetica, sans-serif;
            background: white;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          body {
            display: flex;
            justify-content: center;
            align-items: flex-start;
            padding: 0;
          }
          .label-container {
            width: 100%;
            max-width: 190mm;
            height: calc(297mm - ${topMargin}mm - 10mm);
            max-height: calc(297mm - ${topMargin}mm - 10mm);
            border: 2px solid black;
            padding: 6mm;
            display: flex;
            flex-direction: column;
            overflow: hidden;
            page-break-inside: avoid;
          }
          .header {
            text-align: center;
            margin-bottom: 4mm;
            padding-bottom: 3mm;
            border-bottom: 2px solid black;
            flex-shrink: 0;
          }
          .company-name {
            font-size: 20pt;
            font-weight: bold;
          }
          .section {
            margin-bottom: 3mm;
            flex-shrink: 0;
          }
          .section-title {
            font-size: 9pt;
            font-weight: bold;
            color: #555;
            text-transform: uppercase;
            margin-bottom: 1mm;
          }
          .section-content {
            font-size: ${addressFontSize};
            line-height: 1.3;
          }
          .large-text {
            font-size: 18pt;
            font-weight: bold;
          }
          .country-origin {
            font-size: 22pt;
            font-weight: bold;
            text-align: center;
            padding: 3mm;
            margin: 3mm 0;
            border: 2px solid black;
            background: #f5f5f5;
            flex-shrink: 0;
          }
          .po-number {
            font-size: 20pt;
            font-weight: bold;
            text-align: center;
            padding: 3mm;
            margin: 3mm 0;
            border: 2px solid black;
            flex-shrink: 0;
          }
          .refrigeration-warning {
            font-size: 16pt;
            font-weight: bold;
            text-align: center;
            color: #cc0000;
            padding: 3mm;
            margin-top: auto;
            border: 2px solid #cc0000;
            background: #fff0f0;
            flex-shrink: 0;
          }
          .pallet-info {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 3mm;
            background: #f0f0f0;
            border: 1px solid #ccc;
            margin-bottom: 3mm;
            flex-shrink: 0;
          }
          .pallet-number {
            font-size: 18pt;
            font-weight: bold;
          }
          .cases-count {
            font-size: 16pt;
            font-weight: bold;
          }
          .contents-section {
            flex: 1;
            min-height: 0;
            margin-bottom: 3mm;
            display: flex;
            flex-direction: column;
          }
          .contents-box {
            border: 1px solid #ccc;
            padding: 3mm;
            flex: 1;
            white-space: pre-line;
            font-size: ${contentFontSize};
            line-height: 1.3;
            overflow: hidden;
          }
          .delivery-address {
            white-space: pre-line;
            line-height: 1.3;
          }
          @media print {
            body {
              height: auto;
            }
            .label-container {
              page-break-after: avoid;
              page-break-inside: avoid;
            }
          }
        </style>
      </head>
      <body>
        <div class="label-container">
          ${showCompanyLogo ? `
          <div class="header">
            <div class="company-name">${settings.company_name || 'Cashel Blue'}</div>
          </div>
          ` : ''}
          
          <div class="section">
            <div class="section-title">Deliver To</div>
            <div class="section-content large-text">${labelData.customerName}</div>
            <div class="section-content delivery-address">${labelData.customerDeliveryAddress}</div>
            ${labelData.customerContactName ? `<div class="section-content">Contact: ${labelData.customerContactName}</div>` : ''}
            ${labelData.customerContactPhone ? `<div class="section-content">Tel: ${labelData.customerContactPhone}</div>` : ''}
          </div>
          
          ${showTransporter && labelData.transporterName ? `
          <div class="section">
            <div class="section-title">Transporter</div>
            <div class="section-content large-text">${labelData.transporterName}</div>
            ${labelData.transporterAddress ? `<div class="section-content">${labelData.transporterAddress}</div>` : ''}
          </div>
          ` : ''}
          
          <div class="country-origin">
            COUNTRY OF ORIGIN: ${labelData.countryOfOrigin}
          </div>
          
          ${showContents ? `
          <div class="contents-section">
            <div class="section-title">Contents</div>
            <div class="contents-box">${labelData.contents || '—'}</div>
          </div>
          ` : ''}
          
          <div class="po-number">
            PO: ${labelData.poNumber || '—'}
          </div>
          
          ${(showPalletNumber || showCases) ? `
          <div class="pallet-info">
            ${showPalletNumber ? `<div class="pallet-number">PALLET ${labelData.palletNumber} OF ${labelData.totalPallets}</div>` : ''}
            ${showCases ? `<div class="cases-count">${labelData.totalCases} CASES</div>` : ''}
          </div>
          ` : ''}
          
          <div class="refrigeration-warning">
            ${labelData.refrigerationWarning}
          </div>
        </div>
      </body>
      </html>
    `;
  };

  const handleDownloadPDF = async () => {
    // For now, use print to PDF functionality
    // A proper PDF generation would use the backend
    handlePrint();
    toast.info('Use "Save as PDF" in the print dialog');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6" data-testid="pallet-label-designer-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="btn-back">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-xl font-semibold flex items-center gap-2">
              <Package className="w-5 h-5" />
              Pallet Label Designer
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Design and print A4 pallet labels
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => setEditorMode(!editorMode)}
            data-testid="btn-toggle-editor"
          >
            <Edit className="w-4 h-4 mr-2" />
            {editorMode ? 'Preview Mode' : 'Editor Mode'}
          </Button>
          {canEdit(user?.role) && (
            <Button onClick={handleSaveTemplate} disabled={saving} data-testid="btn-save-template">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Template
            </Button>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Left Panel - Settings & Editor */}
        <div className="space-y-4">
          {/* Dispatch Selection */}
          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-sm">Select Dispatch</CardTitle>
              <CardDescription className="text-xs">Choose a dispatch to generate pallet label</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Select value={selectedDispatchId} onValueChange={setSelectedDispatchId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a dispatch..." />
                </SelectTrigger>
                <SelectContent>
                  {dispatches.map(d => (
                    <SelectItem key={d.id} value={d.id}>
                      #{d.dispatch_number} - {d.customer_name} ({d.dispatch_date})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {dispatch && (
                <div className="text-sm text-muted-foreground space-y-1 p-3 bg-muted/50 rounded-md">
                  <p><strong>Customer:</strong> {dispatch.customer_name}</p>
                  <p><strong>PO:</strong> {dispatch.po_number}</p>
                  <p><strong>Products:</strong> {dispatch.lines?.length || 0} items</p>
                </div>
              )}
            </CardContent>
          </Card>

          {editorMode && (
            <>
              {/* Label Content Editor */}
              <Card>
                <CardHeader className="py-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Edit className="w-4 h-4" />
                    Label Content Editor
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Customer Name</Label>
                      <Input
                        value={labelData.customerName}
                        onChange={(e) => setLabelData({ ...labelData, customerName: e.target.value })}
                        data-testid="input-customer-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>PO Number</Label>
                      <Input
                        value={labelData.poNumber}
                        onChange={(e) => setLabelData({ ...labelData, poNumber: e.target.value })}
                        className="font-mono font-bold"
                        data-testid="input-po-number"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Delivery Address</Label>
                    <Textarea
                      value={labelData.customerDeliveryAddress}
                      onChange={(e) => setLabelData({ ...labelData, customerDeliveryAddress: e.target.value })}
                      rows={3}
                      data-testid="input-delivery-address"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Contact Name</Label>
                      <Input
                        value={labelData.customerContactName}
                        onChange={(e) => setLabelData({ ...labelData, customerContactName: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Contact Phone</Label>
                      <Input
                        value={labelData.customerContactPhone}
                        onChange={(e) => setLabelData({ ...labelData, customerContactPhone: e.target.value })}
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Transporter Name</Label>
                      <Input
                        value={labelData.transporterName}
                        onChange={(e) => setLabelData({ ...labelData, transporterName: e.target.value })}
                        data-testid="input-transporter"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Transporter Address</Label>
                      <Input
                        value={labelData.transporterAddress}
                        onChange={(e) => setLabelData({ ...labelData, transporterAddress: e.target.value })}
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Country of Origin</Label>
                    <Input
                      value={labelData.countryOfOrigin}
                      onChange={(e) => setLabelData({ ...labelData, countryOfOrigin: e.target.value.toUpperCase() })}
                      className="font-bold text-lg"
                      data-testid="input-country-origin"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Contents</Label>
                    <Textarea
                      value={labelData.contents}
                      onChange={(e) => setLabelData({ ...labelData, contents: e.target.value })}
                      rows={4}
                      placeholder="Product Name Format - Qty: X&#10;Product Name Format - Qty: Y"
                      data-testid="input-contents"
                    />
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Pallet #</Label>
                      <Input
                        type="number"
                        min="1"
                        value={labelData.palletNumber}
                        onChange={(e) => setLabelData({ ...labelData, palletNumber: e.target.value })}
                        data-testid="input-pallet-number"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Total Pallets</Label>
                      <Input
                        type="number"
                        min="1"
                        value={labelData.totalPallets}
                        onChange={(e) => setLabelData({ ...labelData, totalPallets: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Total Cases</Label>
                      <Input
                        value={labelData.totalCases}
                        onChange={(e) => setLabelData({ ...labelData, totalCases: e.target.value })}
                        data-testid="input-total-cases"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-red-600">Refrigeration Warning</Label>
                    <Input
                      value={labelData.refrigerationWarning}
                      onChange={(e) => setLabelData({ ...labelData, refrigerationWarning: e.target.value.toUpperCase() })}
                      className="font-bold text-red-600 border-red-300"
                      data-testid="input-refrigeration-warning"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Template Settings */}
              <Card>
                <CardHeader className="py-3">
                  <CardTitle className="text-sm">Template Settings</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Top Margin (mm) - for taping</Label>
                    <Input
                      type="number"
                      min="10"
                      max="50"
                      value={template.topMarginMm}
                      onChange={(e) => setTemplate({ ...template, topMarginMm: parseInt(e.target.value) || 30 })}
                    />
                  </div>
                  
                  <div className="space-y-2 pt-2 border-t">
                    <Label className="text-xs text-muted-foreground">Visible Sections</Label>
                    <div className="flex items-center gap-2">
                      <Checkbox 
                        id="showLogo"
                        checked={template.showCompanyLogo !== false}
                        onCheckedChange={(checked) => setTemplate({ ...template, showCompanyLogo: checked })}
                      />
                      <label htmlFor="showLogo" className="text-sm">Company Name</label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox 
                        id="showTransporter"
                        checked={template.showTransporter !== false}
                        onCheckedChange={(checked) => setTemplate({ ...template, showTransporter: checked })}
                      />
                      <label htmlFor="showTransporter" className="text-sm">Transporter Section</label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox 
                        id="showContents"
                        checked={template.showContents !== false}
                        onCheckedChange={(checked) => setTemplate({ ...template, showContents: checked })}
                      />
                      <label htmlFor="showContents" className="text-sm">Contents List</label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox 
                        id="showPalletNumber"
                        checked={template.showPalletNumber !== false}
                        onCheckedChange={(checked) => setTemplate({ ...template, showPalletNumber: checked })}
                      />
                      <label htmlFor="showPalletNumber" className="text-sm">Pallet Number</label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox 
                        id="showCases"
                        checked={template.showCases !== false}
                        onCheckedChange={(checked) => setTemplate({ ...template, showCases: checked })}
                      />
                      <label htmlFor="showCases" className="text-sm">Cases Count</label>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Aldi Template Settings */}
              <Card>
                <CardHeader className="py-3 flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-sm">Aldi Template Settings</CardTitle>
                    <CardDescription className="text-xs">Customise the "From" address and contact info on Aldi delivery dockets</CardDescription>
                  </div>
                  <Button variant="ghost" size="sm" onClick={handleResetAldi} data-testid="btn-reset-aldi">
                    <RefreshCw className="w-3 h-3 mr-1" /> Reset
                  </Button>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">From Address</Label>
                    <Input
                      placeholder="Line 1 (e.g. J & L Grubb Ltd)"
                      value={aldiSettings.from_line1}
                      onChange={(e) => setAldiSettings({ ...aldiSettings, from_line1: e.target.value })}
                      data-testid="aldi-from-line1"
                    />
                    <Input
                      placeholder="Line 2 (e.g. t/a Cashel Farmhouse Cheesemakers)"
                      value={aldiSettings.from_line2}
                      onChange={(e) => setAldiSettings({ ...aldiSettings, from_line2: e.target.value })}
                      data-testid="aldi-from-line2"
                    />
                    <Input
                      placeholder="Line 3 (e.g. Beechmount, Fethard)"
                      value={aldiSettings.from_line3}
                      onChange={(e) => setAldiSettings({ ...aldiSettings, from_line3: e.target.value })}
                      data-testid="aldi-from-line3"
                    />
                    <Input
                      placeholder="Line 4 (e.g. Co. Tipperary, Ireland)"
                      value={aldiSettings.from_line4}
                      onChange={(e) => setAldiSettings({ ...aldiSettings, from_line4: e.target.value })}
                      data-testid="aldi-from-line4"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">Supplier Number</Label>
                    <Input
                      placeholder="e.g. 04854 (falls back to customer accounts ref)"
                      value={aldiSettings.supplier_number}
                      onChange={(e) => setAldiSettings({ ...aldiSettings, supplier_number: e.target.value })}
                      data-testid="aldi-supplier-number"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="space-y-1">
                      <Label className="text-xs">Contact Name</Label>
                      <Input
                        placeholder="e.g. Nora Griffin"
                        value={aldiSettings.contact_name}
                        onChange={(e) => setAldiSettings({ ...aldiSettings, contact_name: e.target.value })}
                        data-testid="aldi-contact-name"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Contact Tel</Label>
                      <Input
                        placeholder="e.g. 00353 45 846326"
                        value={aldiSettings.contact_tel}
                        onChange={(e) => setAldiSettings({ ...aldiSettings, contact_tel: e.target.value })}
                        data-testid="aldi-contact-tel"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Contact Email</Label>
                      <Input
                        placeholder="e.g. orders@aldi.ie"
                        value={aldiSettings.contact_email}
                        onChange={(e) => setAldiSettings({ ...aldiSettings, contact_email: e.target.value })}
                        data-testid="aldi-contact-email"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Agropur Template Settings */}
              <Card>
                <CardHeader className="py-3 flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-sm">Agropur Template Settings</CardTitle>
                    <CardDescription className="text-xs">Customise the Agropur pallet label header and address</CardDescription>
                  </div>
                  <Button variant="ghost" size="sm" onClick={handleResetAgropur} data-testid="btn-reset-agropur">
                    <RefreshCw className="w-3 h-3 mr-1" /> Reset
                  </Button>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Customer/Header Name</Label>
                      <Input
                        value={agropurSettings.customer_name}
                        onChange={(e) => setAgropurSettings({ ...agropurSettings, customer_name: e.target.value })}
                        data-testid="agropur-customer-name"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Country</Label>
                      <Input
                        value={agropurSettings.address_country}
                        onChange={(e) => setAgropurSettings({ ...agropurSettings, address_country: e.target.value })}
                        data-testid="agropur-country"
                      />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">Address</Label>
                    <Input
                      placeholder="Address Line 1"
                      value={agropurSettings.address_line1}
                      onChange={(e) => setAgropurSettings({ ...agropurSettings, address_line1: e.target.value })}
                      data-testid="agropur-address-line1"
                    />
                    <Input
                      placeholder="Address Line 2"
                      value={agropurSettings.address_line2}
                      onChange={(e) => setAgropurSettings({ ...agropurSettings, address_line2: e.target.value })}
                      data-testid="agropur-address-line2"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">COA Email</Label>
                    <Input
                      value={agropurSettings.coa_email}
                      onChange={(e) => setAgropurSettings({ ...agropurSettings, coa_email: e.target.value })}
                      data-testid="agropur-coa-email"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Supplier Name</Label>
                    <Input
                      value={agropurSettings.supplier_name}
                      onChange={(e) => setAgropurSettings({ ...agropurSettings, supplier_name: e.target.value })}
                      data-testid="agropur-supplier-name"
                    />
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          {/* Quick Actions */}
          {!editorMode && dispatch && (
            <Card>
              <CardHeader className="py-3">
                <CardTitle className="text-sm">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button 
                  className="w-full" 
                  onClick={() => setEditorMode(true)}
                  variant="outline"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Label Content
                </Button>
                <Button 
                  className="w-full"
                  onClick={() => loadDispatchData(selectedDispatchId)}
                  variant="outline"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Reload from Dispatch
                </Button>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Panel - Preview & Print */}
        <div className="space-y-4">
          <Card className="overflow-hidden">
            <CardHeader className="py-3 flex flex-row items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2">
                <Eye className="w-4 h-4" />
                Label Preview
              </CardTitle>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={handleDownloadPDF} data-testid="btn-download-pdf">
                  <FileText className="w-4 h-4 mr-2" />
                  PDF
                </Button>
                <Button size="sm" onClick={handlePrint} data-testid="btn-print">
                  <Printer className="w-4 h-4 mr-2" />
                  Print
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {/* A4 Preview (scaled down) */}
              <div className="bg-gray-100 p-4 overflow-auto max-h-[70vh]">
                <div 
                  ref={printRef}
                  className="bg-white shadow-lg mx-auto border-2 border-black"
                  style={{ 
                    width: '210mm', 
                    height: '297mm',
                    transform: 'scale(0.4)',
                    transformOrigin: 'top center',
                    padding: `${template.topMarginMm || 20}mm 10mm 10mm 10mm`,
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  {/* Label Container */}
                  <div className="border-2 border-black p-4 flex-1 flex flex-col overflow-hidden">
                    {/* Header */}
                    {template.showCompanyLogo !== false && (
                      <div className="text-center mb-3 pb-2 border-b-2 border-black flex-shrink-0">
                        <div className="text-2xl font-bold">{settings.company_name || 'Cashel Blue'}</div>
                      </div>
                    )}
                    
                    {/* Deliver To */}
                    <div className="mb-3 flex-shrink-0">
                      <div className="text-[9pt] font-bold text-gray-600 uppercase mb-1">Deliver To</div>
                      <div className="text-xl font-bold">{labelData.customerName || '—'}</div>
                      <div className="text-sm whitespace-pre-line leading-tight">{labelData.customerDeliveryAddress || '—'}</div>
                      {labelData.customerContactName && <div className="text-sm">Contact: {labelData.customerContactName}</div>}
                      {labelData.customerContactPhone && <div className="text-sm">Tel: {labelData.customerContactPhone}</div>}
                    </div>
                    
                    {/* Transporter */}
                    {template.showTransporter !== false && labelData.transporterName && (
                      <div className="mb-3 flex-shrink-0">
                        <div className="text-[9pt] font-bold text-gray-600 uppercase mb-1">Transporter</div>
                        <div className="text-xl font-bold">{labelData.transporterName}</div>
                        {labelData.transporterAddress && <div className="text-sm">{labelData.transporterAddress}</div>}
                      </div>
                    )}
                    
                    {/* Country of Origin - BOLD AND BIG */}
                    <div className="text-center text-2xl font-bold p-2 my-2 border-2 border-black bg-gray-100 flex-shrink-0">
                      COUNTRY OF ORIGIN: {labelData.countryOfOrigin || 'IRELAND'}
                    </div>
                    
                    {/* Contents */}
                    {template.showContents !== false && (
                      <div className="mb-3 flex-1 min-h-0 flex flex-col">
                        <div className="text-[9pt] font-bold text-gray-600 uppercase mb-1 flex-shrink-0">Contents</div>
                        <div className="border border-gray-300 p-2 flex-1 whitespace-pre-line text-xs overflow-hidden">
                          {labelData.contents || '—'}
                        </div>
                      </div>
                    )}
                    
                    {/* PO Number - BOLD AND BIG */}
                    <div className="text-center text-xl font-bold p-2 my-2 border-2 border-black flex-shrink-0">
                      PO: {labelData.poNumber || '—'}
                    </div>
                    
                    {/* Pallet Info */}
                    {(template.showPalletNumber !== false || template.showCases !== false) && (
                      <div className="flex justify-between items-center p-2 bg-gray-100 border border-gray-300 mb-3 flex-shrink-0">
                        {template.showPalletNumber !== false && (
                          <div className="text-lg font-bold">PALLET {labelData.palletNumber} OF {labelData.totalPallets}</div>
                        )}
                        {template.showCases !== false && (
                          <div className="text-base font-bold">{labelData.totalCases || '0'} CASES</div>
                        )}
                      </div>
                    )}
                    
                    {/* Refrigeration Warning - RED, BOLD, BIG */}
                    <div className="text-center text-lg font-bold p-2 mt-auto border-2 border-red-600 bg-red-50 text-red-600 flex-shrink-0">
                      {labelData.refrigerationWarning || 'PLEASE REFRIGERATE ON DELIVERY'}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
